import { Modal } from "components";
import CountryDropdown from "components/dropdowns/reactselect/CountryDropdown";
import StateDropdown from "components/dropdowns/reactselect/StateDropdown";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React from "react";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";
import { Message } from "semantic-ui-react";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { isNullOrEmptyString } from "utilities";
import { ContactName } from "../detail/components/ContactName";
import MultiEmailInput from "components/ValidatedFields/MultiEmailInput";

interface Props {
  onCreated: (contact: server.dto.Contact) => void;
  onClose: () => void;

  defaults?: Partial<NewContactFormState>
}

interface NewContactFormState {
  firstName: string;
  insertion: string;
  lastName: string;
  emailAddresses: string;
  mobileNumbers: string;
  language: server.dto.LCID
  position: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  country: server.dto.Country;
  state: server.dto.State;
}

const ContactNewModal = (props: Props) => {
  const initialCountry = serverReference.countries.find(c => c.key === site.userPreference.regional.country);
  const initialState = initialCountry.states.find(s => s.isCountryDefault);
  const [formState, setFormState] = React.useState<NewContactFormState>({
    firstName: "",
    insertion: "",
    lastName: "",
    emailAddresses: "",
    mobileNumbers: "",
    language: initialState.culture,
    position: "",
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
    city: "",
    country: initialCountry,
    state: initialState,
    ...props.defaults
  });

  const onChange = (newData: Partial<NewContactFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }


  const submit = () => {
    return window.CoreApi.Contact.Create({
      name: {
        first: formState.firstName,
        insertion: formState.insertion,
        last: formState.lastName,
      },
      emailAddresses: formState.emailAddresses,
      mobileNumbers: convertToPhoneNumbers(formState.mobileNumbers),
      culture: formState.language,
      position: formState.position,
      address: {
        addressLine1: formState.addressLine1,
        addressLine2: formState.addressLine2,
        postalCode: formState.postalCode,
        city: formState.city,
        stateId: formState.state.key,
      }
    });
  };

  const onSuccess = async (contactId: number) => {
    const res = await window.CoreApi.Contact.Contact(contactId).then(r => r.data);
    props.onCreated(res.data);
  }

  const debouncedContactName = useDebounce(
    [formState.firstName, formState.insertion, formState.lastName]
      .map(n => n.trim())
      .filter(n => !isNullOrEmptyString(n))
      .join(" "),
    500);

  const contactExistsQuery = useCustomQuery({
    queryKey: ["contactExists", debouncedContactName],
    queryFn: () => window.Api.Contacts.NameExists(debouncedContactName).then(r => r.data),
    enabled: Boolean(debouncedContactName),
  });


  return (
    <Modal
      isOpen
      title={site.currentEnvironment.contact.newLabel}
      handleClose={props.onClose}
      width="tiny"
      hasAutoScrollBox
      minHeight={400}
    >
      <div className="grow1 scrollbox-auto" >
        <EditSectionTemplate
          submit={submit}
          noPanels
          onSuccess={onSuccess}
          sections={[
            {
              key: "contactDetails",
              type: "regular",
              title: site.currentEnvironment.contact.newLabel,
              fields: [
                {
                  key: "name",
                  label: RESX.GeneralLabels.Name,
                  renderEditNew: () => (
                    <ContactName
                      firstName={formState.firstName}
                      insertion={formState.insertion}
                      lastName={formState.lastName}
                      onChange={(firstName, insertion, lastName) => onChange({
                        firstName,
                        insertion,
                        lastName
                      })}
                    />
                  )
                }, {
                  key: "emailAddresses",
                  label: RESX.GeneralLabels.Email,
                  renderEditNew: () => (
                    <MultiEmailInput
                      value={formState.emailAddresses}
                      onChange={(value) => onChange({ emailAddresses: value })}
                      validatorKey="contactEmails"
                      maxLength={serverReference.constants.mailAddress.maxEmailLength}
                    />
                  )
                }, {
                  key: "mobileNumbers",
                  label: RESX.Contact.Mobile,
                  renderEditNew: () => (
                    <PhoneInput
                      value={formState.mobileNumbers}
                      onChange={(value) => onChange({ mobileNumbers: value })}
                    />
                  )
                }, {
                  key: "language",
                  label: RESX.Language.resxLanguage,
                  renderEditNew: () => (
                    <CulturesTemplateDropdown
                      value={formState.language}
                      onChange={(value) => onChange({ language: value })}
                    />
                  )
                }, {
                  key: "position",
                  label: RESX.Contact.Position,
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.contact.maxPositionLength}
                      value={formState.position}
                      onChange={(value) => onChange({ position: value })}
                    />
                  )
                }, {
                  key: "addressLine1",
                  label: RESX.Address.AddressLine + " 1",
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxAddressLineLength}
                      value={formState.addressLine1}
                      onChange={(value) => onChange({ addressLine1: value })}
                    />
                  )
                },
                {
                  key: "addressLine2",
                  label: RESX.Address.AddressLine + " 2",
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxAddressLineLength}
                      value={formState.addressLine2}
                      onChange={(value) => onChange({ addressLine2: value })}
                    />
                  )
                }, {
                  key: "postalCode",
                  label: RESX.Address.PostalCode,
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxPostalCodeLength}
                      value={formState.postalCode}
                      onChange={(value) => onChange({ postalCode: value })}
                    />
                  )
                }, {
                  key: "city",
                  label: RESX.Address.City,
                  renderEditNew: () => (
                    <OptionalTextInput
                      maxLength={serverReference.constants.address.maxCityLength}
                      value={formState.city}
                      onChange={(value) => onChange({ city: value })}
                    />
                  )
                }, {
                  key: "country",
                  label: RESX.Address.Country,
                  renderEditNew: () => (
                    <CountryDropdown
                      value={formState.country}
                      onChange={(value) => onChange({
                        country: value,
                        state: value.states.find(s => s.isCountryDefault),
                        language: value.states.find(s => s.isCountryDefault).culture
                      })}
                    />
                  )
                }, {
                  key: "state",
                  label: RESX.Address.State,
                  renderEditNew: () => (
                    <StateDropdown
                      value={formState.state}
                      options={formState.country?.states ?? []}
                      onChange={(value) => onChange({ state: value })}
                    />
                  )
                }
              ],
            }, {
              key: "companyExistsWarning",
              type: "custom",
              hidden: !contactExistsQuery.data,
              render: () => <Message color="red" className="margin-top margin-bottom">
                {RESX.Contact.AContactWithThisNameAlreadyExists}
              </Message>
            }
          ]}
        />
      </div>
    </Modal >
  );
}

export default ContactNewModal;